import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { useStorytelling } from '@/context/StorytellingContext'
import { ChapterCard } from '@/components/storytelling/ChapterCard'
import { chapterMap } from '@/data/chapters'

export function StorytellingHome() {
  const { chapterStatuses, unlockedPlaygrounds, isLoading } = useStorytelling()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <p style={{ color: 'var(--color-story-text-muted)' }}>Loading your journey...</p>
      </div>
    )
  }

  const completedCount = chapterStatuses.filter((s) => s.status === 'completed').length

  return (
    <div className="max-w-lg mx-auto px-5 py-6">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="font-display text-2xl font-bold" style={{ color: 'var(--color-story-text)' }}>Story Secrets</h1>
        <p className="text-sm mt-1" style={{ color: 'var(--color-story-text-muted)' }}>Ever wonder why some stories won't let you go?</p>
      </motion.div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="flex items-center gap-2 mt-4 mb-6">
        <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--color-story-border)' }}>
          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${(completedCount / chapterMap.length) * 100}%`, background: 'linear-gradient(90deg, var(--color-story-primary), var(--color-story-primary-light))' }} />
        </div>
        <span className="text-xs font-semibold" style={{ color: 'var(--color-story-text-muted)' }}>
          {completedCount > 0 ? `You've unlocked ${completedCount} story secret${completedCount !== 1 ? 's' : ''}` : 'Start your journey'}
        </span>
      </motion.div>

      <div className="flex flex-col gap-4">
        {chapterMap.map((chapter, index) => {
          const statusEntry = chapterStatuses.find((s) => s.chapterId === chapter.id)
          return (
            <motion.div key={chapter.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 + index * 0.05 }}>
              <ChapterCard index={index} id={chapter.id} title={chapter.title} hook={chapter.hook} payoff={chapter.payoff} status={statusEntry?.status ?? 'locked'} />
            </motion.div>
          )
        })}
      </div>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="mt-8 pt-5" style={{ borderTop: '2px dashed var(--color-story-border)' }}>
        <div className="flex items-center gap-2 mb-3">
          <span className="text-lg">🎭</span>
          <h2 className="font-display text-lg font-bold" style={{ color: 'var(--color-story-text)' }}>Story Playground</h2>
        </div>
        <p className="text-sm mb-4" style={{ color: 'var(--color-story-text-muted)' }}>Try your own story ideas. New activities unlock as you complete chapters.</p>

        {unlockedPlaygrounds.includes('branches') ? (
          <Link to="/storytelling/play/branches" className="flex items-center gap-3 bg-white rounded-xl p-4 transition-shadow hover:shadow-md" style={{ border: '1px solid var(--color-story-border)' }}>
            <div className="w-9 h-9 rounded-lg flex items-center justify-center text-lg" style={{ background: 'linear-gradient(135deg, var(--color-story-primary), var(--color-story-primary-light))' }}>🌿</div>
            <div className="flex-1">
              <p className="font-semibold text-sm" style={{ color: 'var(--color-story-text)' }}>Story Branches</p>
              <p className="text-xs" style={{ color: 'var(--color-story-text-muted)' }}>Shape a story through your choices</p>
            </div>
            <span className="text-xs font-semibold" style={{ color: 'var(--color-story-primary)' }}>Play →</span>
          </Link>
        ) : (
          <div className="flex items-center gap-3 rounded-xl p-4 opacity-50" style={{ background: 'var(--color-story-bg-light)', border: '1px solid var(--color-story-border)' }}>
            <div className="w-9 h-9 rounded-lg flex items-center justify-center text-lg" style={{ background: 'var(--color-story-locked)' }}>🌿</div>
            <div className="flex-1">
              <p className="font-semibold text-sm" style={{ color: 'var(--color-story-text)' }}>Story Branches</p>
              <p className="text-xs" style={{ color: 'var(--color-story-text-muted)' }}>Shape a story through your choices</p>
            </div>
            <span className="text-xs" style={{ color: 'var(--color-story-primary-light)' }}>Chapter 1</span>
          </div>
        )}

        <div className="flex items-center gap-3 rounded-xl p-4 mt-3 opacity-50" style={{ background: 'var(--color-story-bg-light)', border: '1px solid var(--color-story-border)' }}>
          <div className="w-9 h-9 rounded-lg flex items-center justify-center text-lg" style={{ background: 'var(--color-story-locked)' }}>🔄</div>
          <div className="flex-1">
            <p className="font-semibold text-sm" style={{ color: 'var(--color-story-text)' }}>Story Remix</p>
            <p className="text-xs" style={{ color: 'var(--color-story-text-muted)' }}>Twist a story — change the villain, flip the ending</p>
          </div>
          <span className="text-xs" style={{ color: 'var(--color-story-primary-light)' }}>Chapter 5</span>
        </div>
      </motion.div>
    </div>
  )
}
